import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList, CartesianGrid } from "recharts";
import { Gauge, Clock, Send, Trophy, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { APP_STATUTS } from "./ApplicationsTracker";

const HEX = { a_envoyer: "#94a3b8", envoyee: "#0284c7", entretien: "#d97706", acceptee: "#059669", refusee: "#dc2626", sans_reponse: "#64748b" };
const MOIS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const fmtMonth = (m) => { const [y, mm] = m.split("-"); return `${MOIS[parseInt(mm) - 1]} ${y.slice(2)}`; };

const Kpi = ({ icon: I, label, value, hint, testId, tone = "text-navy" }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testId}>
    <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500"><I size={12} className="text-[#0E7C7B]" /> {label}</p>
    <p className={`mt-1 text-2xl font-bold ${tone}`}>{value}</p>
    {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
  </div>
);

export function ApplicationsDashboard({ refreshKey }) {
  const [st, setSt] = useState(null);
  useEffect(() => { api.get("/cv/applications/stats").then(({ data }) => setSt(data)).catch(() => setSt({ total: 0, funnel: [] })); }, [refreshKey]);
  if (!st) return <div className="mt-3 flex justify-center py-6"><Loader2 size={18} className="animate-spin text-slate-400" /></div>;
  if (!st.total) return <p className="mt-3 rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400" data-testid="cv-dash-empty">Le tableau de bord s'affichera dès votre première candidature enregistrée.</p>;
  const funnel = (st.funnel || []).map((f) => ({ ...f, label: APP_STATUTS.find((s) => s.v === f.statut)?.l || f.statut }));
  const pct = (v) => (v == null ? "—" : `${v} %`);
  const days = (v) => (v == null ? "—" : `${v} j`);
  return (
    <div className="mt-3 space-y-3" data-testid="cv-dashboard">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={Gauge} label="Taux de réponse" value={pct(st.response_rate)} hint={`${st.responded} réponse(s) sur ${st.sent} envoyée(s)`} testId="cv-dash-response-rate" />
        <Kpi icon={Clock} label="Délai moyen de réponse" value={days(st.avg_response_days)} hint={st.median_response_days != null ? `médiane ${st.median_response_days} j · ${st.pending} en attente${st.avg_waiting_days != null ? ` (${st.avg_waiting_days} j en moyenne)` : ""}` : `${st.pending} en attente`} testId="cv-dash-delay" />
        <Kpi icon={Send} label="Succès des relances" value={pct(st.relance_success_rate)} hint={`${st.relance_success} réponse(s) après relance sur ${st.relanced} relancée(s)`} testId="cv-dash-relance-rate" tone="text-[#0E7C7B]" />
        <Kpi icon={Trophy} label="Entretiens / acceptées" value={`${st.interviews} / ${st.accepted}`} hint={st.acceptance_rate != null ? `taux d'acceptation ${st.acceptance_rate} %` : ""} testId="cv-dash-interviews" tone="text-emerald-700" />
      </div>
      <div className="grid gap-3 lg:grid-cols-[3fr_2fr]">
        <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="cv-dash-funnel">
          <p className="text-sm font-semibold text-navy">Entonnoir par statut</p>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={funnel} layout="vertical" margin={{ left: 4, right: 32, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="label" width={96} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => [`${v} candidature(s)`, ""]} />
              <Bar dataKey="n" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                {funnel.map((f) => <Cell key={f.statut} fill={HEX[f.statut]} />)}
                <LabelList dataKey="n" position="right" style={{ fontSize: 11, fill: "#0E2540" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="cv-dash-months">
          <p className="text-sm font-semibold text-navy">Candidatures envoyées par mois</p>
          {(st.by_month || []).length === 0 ? <p className="py-8 text-center text-xs text-slate-400">Aucune date d'envoi renseignée.</p> : (
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={st.by_month} margin={{ left: 4, right: 8, top: 12, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip labelFormatter={fmtMonth} formatter={(v) => [`${v} envoi(s)`, ""]} />
                <Bar dataKey="n" fill="#0E7C7B" radius={[4, 4, 0, 0]} isAnimationActive={false}><LabelList dataKey="n" position="top" style={{ fontSize: 10, fill: "#0E2540" }} /></Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
